import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';
import {
  Image,
  FileText,
  Scan,
  Calendar,
  ChevronDown,
  ChevronUp,
  Stethoscope,
  ExternalLink,
  Loader,
  AlertCircle,
} from 'lucide-react';
import { useDocumentViewer } from '../../contexts/DocumentViewerContext';
import { fromRAGCitation, type UnifiedCitation } from '../../types/citation';
import type { DocumentViewRequest } from '../../types/citation';
import type { DicomStudy, DicomInstanceInfo, RAGCitation } from '../../types';
import { documentApi } from '../../services/api';
import { DicomStudyCard } from '../dicom/DicomStudyCard';
import { DicomViewer } from '../dicom/DicomViewer';
import { MarkdownWithCitations } from '../citations/MarkdownWithCitations';

interface DocumentTypeClassification {
  documentId: string;
  type: string;
  confidence: number;
  summary?: string;
}

interface MetadataExtractionResponse {
  patients: any[];
  keyDates: any[];
  documentTypes: DocumentTypeClassification[];
  pertinentFacts?: Record<string, any[]>;
  caseSummary?: string;
  confidence: number;
  reasoning: string;
}

interface DocumentInfo {
  documentId: string;
  filename: string;
  pageCount: number;
}

interface CombinedScan {
  date: string;
  headline: string;
  description: string;
  sourceDocumentId?: string;
  sourceName?: string;
  pageNumber?: number;
  confidence: number;
  source: 'chronology' | 'keyDate';
  citations: UnifiedCitation[];
}

interface ImagingOverviewProps {
  caseId: string;
  extractedImageCount: number;
}

const IMAGING_KEYWORDS = [
  'x-ray', 'xray', 'ct scan', 'ct ', 'mri', 'ultrasound', 'scan',
  'echocardiogram', 'echo', 'ecg', 'ekg', 'mammogram', 'pet scan',
  'angiogram', 'fluoroscopy', 'dexa', 'radiograph', 'imaging',
  'radiology', 'nuclear medicine', 'bone scan', 'doppler',
];

function formatDocType(type: string): string {
  return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

/**
 * Derive a short headline from the description text.
 * Looks for a recognisable imaging modality and returns it;
 * falls back to the first sentence (capped at 60 chars).
 */
function deriveHeadline(description: string, fallbackType?: string): string {
  const lower = description.toLowerCase();

  // Ordered by specificity – first match wins
  const modalities: [string, string][] = [
    ['pet scan', 'PET Scan'],
    ['pet-ct', 'PET-CT'],
    ['ct angiogram', 'CT Angiogram'],
    ['ct scan', 'CT Scan'],
    ['ct head', 'CT Head'],
    ['ct thorax', 'CT Thorax'],
    ['ct abdomen', 'CT Abdomen'],
    ['ct chest', 'CT Chest'],
    [' ct ', 'CT Scan'],
    ['mri brain', 'MRI Brain'],
    ['mri spine', 'MRI Spine'],
    ['mri lumbar', 'MRI Lumbar Spine'],
    ['mri cervical', 'MRI Cervical Spine'],
    ['mri knee', 'MRI Knee'],
    ['mri shoulder', 'MRI Shoulder'],
    [' mri ', 'MRI'],
    ['chest x-ray', 'Chest X-Ray'],
    ['chest xray', 'Chest X-Ray'],
    ['x-ray', 'X-Ray'],
    ['xray', 'X-Ray'],
    ['ultrasound', 'Ultrasound'],
    ['echocardiogram', 'Echocardiogram'],
    ['transthoracic echo', 'Transthoracic Echocardiogram'],
    ['echo ', 'Echocardiogram'],
    ['doppler', 'Doppler Ultrasound'],
    ['mammogram', 'Mammogram'],
    ['dexa', 'DEXA Scan'],
    ['bone scan', 'Bone Scan'],
    ['fluoroscopy', 'Fluoroscopy'],
    ['angiogram', 'Angiogram'],
    ['nuclear medicine', 'Nuclear Medicine Study'],
    ['eeg', 'EEG'],
    ['ecg', 'ECG'],
    ['ekg', 'EKG'],
    ['endoscopy', 'Endoscopy'],
    ['colonoscopy', 'Colonoscopy'],
  ];

  for (const [keyword, label] of modalities) {
    if (lower.includes(keyword)) return label;
  }

  // Fallback: use event type if it's not just "TEST" or "PROCEDURE"
  if (fallbackType && !['test', 'procedure', 'investigation'].includes(fallbackType.toLowerCase())) {
    return formatDocType(fallbackType);
  }

  // Last resort: first sentence, capped
  const firstSentence = description.split(/[.!?]/)[0].trim();
  return firstSentence.length > 60 ? firstSentence.substring(0, 57) + '...' : firstSentence;
}

export const ImagingOverview: React.FC<ImagingOverviewProps> = ({ caseId, extractedImageCount }) => {
  const [reportsExpanded, setReportsExpanded] = useState(true);
  const [scansExpanded, setScansExpanded] = useState(true);
  const [dicomExpanded, setDicomExpanded] = useState(true);
  const [viewerStudy, setViewerStudy] = useState<DicomStudy | null>(null);
  const [viewerInstances, setViewerInstances] = useState<DicomInstanceInfo[]>([]);
  const [loadingStudyId, setLoadingStudyId] = useState<string | null>(null);

  const { openDocument } = useDocumentViewer();

  // Fetch DICOM studies for this case
  const { data: dicomStudies, isLoading: dicomLoading, error: dicomError, refetch: refetchDicom } = useQuery(
    ['dicom-studies', caseId],
    () => documentApi.getDicomStudies(caseId),
    {
      staleTime: 30000,
      retry: 3,
      retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 10000),
    }
  );

  const handleOpenDicomViewer = useCallback(async (study: DicomStudy) => {
    setLoadingStudyId(study.id);
    try {
      const detail = await documentApi.getDicomStudy(study.id);
      setViewerStudy(detail);
      setViewerInstances(detail.instances || []);
    } catch (err) {
      console.error('Failed to load DICOM study details:', err);
    } finally {
      setLoadingStudyId(null);
    }
  }, []);

  const handleOpenDocument = useCallback((documentId: string, documentName: string, pageNumber?: number) => {
    if (!documentId) return;
    const request: DocumentViewRequest = {
      documentId,
      documentName,
      pageNumber,
    };
    openDocument(request);
  }, [openDocument]);

  // Read from React Query cache (already fetched by CaseDetailPage)
  const { data: metadata } = useQuery<MetadataExtractionResponse | null>(
    ['metadata', caseId],
    { enabled: false }
  );

  const { data: documentsData } = useQuery<any>(
    ['documents', caseId],
    { enabled: false }
  );

  const { data: timelineData } = useQuery<any>(
    ['timeline', caseId, {}],
    { enabled: false }
  );

  const documents: DocumentInfo[] = useMemo(() => {
    if (!documentsData) return [];
    const docs = Array.isArray(documentsData) ? documentsData : documentsData?.documents || [];
    return docs.map((d: any) => ({
      documentId: d.documentId,
      filename: d.filename,
      pageCount: d.pageCount || 0,
    }));
  }, [documentsData]);

  // Filter imaging reports from metadata document classifications
  const imagingReports = useMemo(() => {
    return (metadata?.documentTypes || []).filter(dt => {
      const upper = dt.type.toUpperCase();
      return upper.includes('IMAGING') || upper.includes('RADIOLOGY');
    });
  }, [metadata?.documentTypes]);

  // Filter imaging events from chronology
  const imagingEvents = useMemo(() => {
    const events = timelineData?.timeline?.events || timelineData?.events || [];
    return events.filter((event: any) => {
      const type = (event.type || event.eventType || '').toLowerCase();
      return type.includes('investigation') || type.includes('imaging');
    });
  }, [timelineData]);

  // Filter imaging-related key dates from metadata
  const imagingKeyDates = useMemo(() => {
    const IMAGING_DATE_TYPES = new Set(['TEST', 'INVESTIGATION', 'PROCEDURE']);
    return (metadata?.keyDates || []).filter((kd: any) => {
      if (!IMAGING_DATE_TYPES.has(kd.type?.toUpperCase())) return false;
      const contextLower = (kd.context || '').toLowerCase();
      return IMAGING_KEYWORDS.some(kw => contextLower.includes(kw));
    });
  }, [metadata?.keyDates]);

  // Merge chronology events and key dates, deduplicate, sort by date desc
  const combinedScans: CombinedScan[] = useMemo(() => {
    const scans: CombinedScan[] = [];

    imagingEvents.forEach((event: any) => {
      const ts = event.timestamp || event.eventDate || '';
      const rawType = event.type || event.eventType || '';
      const desc = event.description || '';
      scans.push({
        date: typeof ts === 'string' ? ts : ts?.start || '',
        headline: deriveHeadline(desc, rawType),
        description: desc,
        sourceDocumentId: event.sourceReference?.documentId || event.citations?.[0]?.documentId,
        sourceName: event.sourceReference?.documentName || event.citations?.[0]?.documentName,
        pageNumber: event.sourceReference?.pageNumber || event.citations?.[0]?.pageNumber,
        confidence: event.extractionConfidence || event.confidence || 0,
        source: 'chronology',
        citations: (event.citations || []).map((c: RAGCitation) => fromRAGCitation(c)),
      });
    });

    imagingKeyDates.forEach((kd: any) => {
      const isDuplicate = scans.some(s =>
        s.date.startsWith(kd.date) &&
        s.description.toLowerCase().includes((kd.context || '').toLowerCase().substring(0, 30))
      );
      if (!isDuplicate) {
        const desc = kd.context || '';
        scans.push({
          date: kd.date || '',
          headline: deriveHeadline(desc, kd.type),
          description: desc,
          sourceDocumentId: kd.citations?.[0]?.documentId,
          sourceName: kd.citations?.[0]?.documentName,
          pageNumber: kd.citations?.[0]?.pageNumber,
          confidence: kd.confidence || 0,
          source: 'keyDate',
          citations: (kd.citations || []).map((c: RAGCitation) => fromRAGCitation(c)),
        });
      }
    });

    return scans.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [imagingEvents, imagingKeyDates]);

  // Compute date range label
  const dateRangeLabel = useMemo(() => {
    const dates = combinedScans
      .map(s => new Date(s.date))
      .filter(d => !isNaN(d.getTime()));
    if (dates.length === 0) return 'N/A';
    const earliest = new Date(Math.min(...dates.map(d => d.getTime())));
    const latest = new Date(Math.max(...dates.map(d => d.getTime())));
    if (earliest.getTime() === latest.getTime()) return formatDate(earliest.toISOString());
    return `${formatDate(earliest.toISOString())} - ${formatDate(latest.toISOString())}`;
  }, [combinedScans]);

  const dicomCount = dicomStudies?.length || 0;

  // Don't render if no metadata at all and no DICOM studies (but wait for DICOM query to finish)
  if (!metadata && extractedImageCount === 0 && dicomCount === 0 && !dicomLoading && !dicomError) return null;

  const hasAnyImagingData = imagingReports.length > 0 || combinedScans.length > 0 || extractedImageCount > 0 || dicomCount > 0 || dicomLoading || !!dicomError;
  if (!hasAnyImagingData) return null;

  return (
    <div className="mb-6 space-y-4">
      {/* DICOM query error banner */}
      {dicomError && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-300">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>DICOM studies could not be loaded.</span>
          <button
            onClick={() => refetchDicom()}
            className="ml-auto text-amber-700 dark:text-amber-400 hover:underline font-medium"
          >
            Retry
          </button>
        </div>
      )}
      {/* Summary Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={Image} label="Images Extracted" value={extractedImageCount} />
        <StatCard icon={FileText} label="Imaging Reports" value={imagingReports.length} />
        <StatCard icon={Stethoscope} label="Scans Identified" value={combinedScans.length} />
        {dicomCount > 0 && <StatCard icon={Scan} label="DICOM Studies" value={dicomCount} />}
        <StatCard icon={Calendar} label="Date Range" textValue={dateRangeLabel} />
      </div>

      {/* Radiologist Reports */}
      {(imagingReports.length > 0 || metadata) && (
        <div className="bg-white dark:bg-medical-800 border border-gray-200 dark:border-medical-700 rounded-lg overflow-hidden">
          <button
            onClick={() => setReportsExpanded(prev => !prev)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-medical-700 transition-colors"
          >
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
              <span className="font-semibold text-gray-900 dark:text-medical-100">Radiologist Reports</span>
              <span className="text-sm text-gray-500 dark:text-medical-400">({imagingReports.length})</span>
            </div>
            {reportsExpanded
              ? <ChevronUp className="w-5 h-5 text-gray-400" />
              : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
          {reportsExpanded && (
            <div className="border-t border-gray-200 dark:border-medical-700">
              {imagingReports.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500 dark:text-medical-400">
                  No imaging reports identified in the case documents.
                </div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-medical-700">
                  {imagingReports.map((report, i) => {
                    const doc = documents.find(d => d.documentId === report.documentId);
                    const filename = doc?.filename || 'Unknown document';
                    return (
                      <div key={i} className="p-4">
                        <div className="flex items-start gap-3">
                          <FileText className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <button
                              onClick={() => handleOpenDocument(report.documentId, filename)}
                              className="font-medium text-cyan-600 dark:text-cyan-400 hover:text-cyan-800 dark:hover:text-cyan-300 hover:underline truncate block text-left max-w-full transition-colors"
                              title={`View ${filename}`}
                            >
                              {filename}
                              <ExternalLink className="w-3.5 h-3.5 inline-block ml-1.5 -mt-0.5 opacity-60" />
                            </button>
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-medical-400 flex-wrap">
                              <span className="px-2 py-0.5 bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 rounded font-medium">
                                {formatDocType(report.type)}
                              </span>
                              <span>{Math.round(report.confidence * 100)}% confidence</span>
                              {doc && <span>{doc.pageCount} {doc.pageCount === 1 ? 'page' : 'pages'}</span>}
                            </div>
                            {report.summary && (
                              <div className="text-sm text-gray-600 dark:text-medical-300 mt-2 leading-relaxed prose prose-sm dark:prose-invert max-w-none">
                                <MarkdownWithCitations
                                  content={report.summary}
                                  citations={[{
                                    id: `report-${report.documentId}`,
                                    documentId: report.documentId,
                                    documentName: doc?.filename || 'Document',
                                    text: '',
                                  }]}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Scans & Investigations */}
      {(combinedScans.length > 0 || metadata) && (
        <div className="bg-white dark:bg-medical-800 border border-gray-200 dark:border-medical-700 rounded-lg overflow-hidden">
          <button
            onClick={() => setScansExpanded(prev => !prev)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-medical-700 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Scan className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <span className="font-semibold text-gray-900 dark:text-medical-100">Scans & Investigations</span>
              <span className="text-sm text-gray-500 dark:text-medical-400">({combinedScans.length})</span>
            </div>
            {scansExpanded
              ? <ChevronUp className="w-5 h-5 text-gray-400" />
              : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
          {scansExpanded && (
            <div className="border-t border-gray-200 dark:border-medical-700">
              {combinedScans.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500 dark:text-medical-400">
                  No scans or investigations identified in the case documentation.
                </div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-medical-700">
                  {combinedScans.map((scan, i) => (
                    <div key={i} className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-2">
                          <div className="w-2 h-2 rounded-full bg-emerald-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <time className="text-xs font-medium text-gray-500 dark:text-medical-400 tabular-nums">
                              {formatDate(scan.date)}
                            </time>
                            <span className="text-gray-300 dark:text-medical-600">&middot;</span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-medical-100">
                              {scan.headline}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-medical-300 leading-relaxed prose prose-sm dark:prose-invert max-w-none">
                            <MarkdownWithCitations
                              content={scan.description}
                              citations={scan.citations}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* DICOM Studies */}
      {dicomCount > 0 && (
        <div className="bg-white dark:bg-medical-800 border border-gray-200 dark:border-medical-700 rounded-lg overflow-hidden">
          <button
            onClick={() => setDicomExpanded(prev => !prev)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-medical-700 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Scan className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
              <span className="font-semibold text-gray-900 dark:text-medical-100">DICOM Studies</span>
              <span className="text-sm text-gray-500 dark:text-medical-400">({dicomCount})</span>
            </div>
            {dicomExpanded
              ? <ChevronUp className="w-5 h-5 text-gray-400" />
              : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
          {dicomExpanded && (
            <div className="border-t border-gray-200 dark:border-medical-700 p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {dicomStudies!.map((study) => (
                  <div key={study.id} className="relative">
                    <DicomStudyCard
                      study={study}
                      onClick={() => handleOpenDicomViewer(study)}
                    />
                    {loadingStudyId === study.id && (
                      <div className="absolute inset-0 bg-white/60 dark:bg-black/40 flex items-center justify-center rounded-lg">
                        <Loader className="w-5 h-5 animate-spin text-cyan-600" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* DICOM Viewer Modal */}
      {viewerStudy && viewerInstances.length > 0 && (
        <DicomViewer
          study={viewerStudy}
          instances={viewerInstances}
          onClose={() => { setViewerStudy(null); setViewerInstances([]); }}
        />
      )}
    </div>
  );
};

// Compact stat card used in the summary row
const StatCard: React.FC<{
  icon: React.FC<{ className?: string }>;
  label: string;
  value?: number;
  textValue?: string;
}> = ({ icon: Icon, label, value, textValue }) => (
  <div className="bg-white dark:bg-medical-800 border border-gray-200 dark:border-medical-700 rounded-lg p-3 flex items-center gap-3">
    <div className="p-2 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg flex-shrink-0">
      <Icon className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
    </div>
    <div className="min-w-0">
      <div className="text-xl font-bold text-gray-900 dark:text-medical-100 truncate">
        {textValue ?? value ?? 0}
      </div>
      <div className="text-xs text-gray-500 dark:text-medical-400">{label}</div>
    </div>
  </div>
);
